import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Paper, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  Add as AddIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Edit as EditIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import AdminService from '../../services/AdminService';

const SERVICE_EMPTY = { mobile: '', email: '', country: '', address: '', timezone: '' };

export default function OrganizationsView() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);

  // Create org
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [ownerId, setOwnerId] = useState('');

  // Service dialog
  const [serviceDialog, setServiceDialog] = useState(null); // { orgId, service? }
  const [serviceForm, setServiceForm] = useState(SERVICE_EMPTY);

  const [deleteServiceTarget, setDeleteServiceTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  function showSnack(message, severity = 'success') {
    setSnackbar({ open: true, message, severity });
  }

  async function fetchOrgs() {
    try {
      const data = await AdminService.getAllOrganizations();
      setOrgs(data || []);
    } catch {
      showSnack('Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchOrgs(); }, []);

  async function toggleExpand(org) {
    if (expanded === org.id) {
      setExpanded(null);
      setExpandedOrg(null);
      return;
    }
    try {
      const full = await AdminService.getOrganization(org.id);
      setExpandedOrg(full);
      setExpanded(org.id);
    } catch {
      showSnack('Failed to load org details', 'error');
    }
  }

  async function handleCreateOrg() {
    try {
      await AdminService.createOrganization(ownerId.trim());
      showSnack('Organization created');
      setCreateOrgOpen(false);
      setOwnerId('');
      fetchOrgs();
    } catch {
      showSnack('Failed to create organization', 'error');
    }
  }

  function openCreateService(orgId) {
    setServiceDialog({ orgId, service: null });
    setServiceForm(SERVICE_EMPTY);
  }

  function openEditService(orgId, service) {
    setServiceDialog({ orgId, service });
    setServiceForm({
      mobile: service.mobile || '',
      email: service.email || '',
      country: service.country || '',
      address: service.address || '',
      timezone: service.timezone || '',
    });
  }

  async function handleSaveService() {
    try {
      if (serviceDialog.service) {
        await AdminService.updateService(serviceDialog.service.id, serviceForm);
        showSnack('Service updated');
      } else {
        await AdminService.createService({ organization_id: serviceDialog.orgId, ...serviceForm });
        showSnack('Service created');
      }
      setServiceDialog(null);
      // Refresh the expanded org
      if (expanded) {
        const full = await AdminService.getOrganization(expanded);
        setExpandedOrg(full);
      }
    } catch {
      showSnack('Failed to save service', 'error');
    }
  }

  async function handleDeleteService() {
    try {
      await AdminService.deleteService(deleteServiceTarget.id);
      showSnack('Service deleted');
      setDeleteServiceTarget(null);
      if (expanded) {
        const full = await AdminService.getOrganization(expanded);
        setExpandedOrg(full);
      }
    } catch {
      showSnack('Failed to delete service', 'error');
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Organizations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOrgOpen(true)}>
          New Organization
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>ID</TableCell>
              <TableCell>Owner ID</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center">Loading…</TableCell></TableRow>
            ) : orgs.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No organizations found</TableCell></TableRow>
            ) : orgs.map((org) => (
              <React.Fragment key={org.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleExpand(org)}>
                      {expanded === org.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{org.id}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{org.owner_id}</TableCell>
                  <TableCell>{org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}</TableCell>
                </TableRow>
                {expanded === org.id && expandedOrg && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 0, bgcolor: '#f9f9f9' }}>
                      <Collapse in timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>Services</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => openCreateService(org.id)}>
                              Add Service
                            </Button>
                          </Box>
                          {(expandedOrg.services || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No services yet</Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Mobile</TableCell>
                                  <TableCell>Email</TableCell>
                                  <TableCell>Country</TableCell>
                                  <TableCell>Timezone</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {expandedOrg.services.map((svc) => (
                                  <TableRow key={svc.id}>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{svc.id}</TableCell>
                                    <TableCell>{svc.mobile || '—'}</TableCell>
                                    <TableCell>{svc.email || '—'}</TableCell>
                                    <TableCell>{svc.country || '—'}</TableCell>
                                    <TableCell>{svc.timezone || '—'}</TableCell>
                                    <TableCell align="right">
                                      <IconButton size="small" onClick={() => openEditService(org.id, svc)}><EditIcon fontSize="small" /></IconButton>
                                      <IconButton size="small" color="error" onClick={() => setDeleteServiceTarget(svc)}><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Org Dialog */}
      <Dialog open={createOrgOpen} onClose={() => setCreateOrgOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Organization</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField label="Owner Account ID" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} fullWidth required />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOrgOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateOrg} disabled={!ownerId.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={!!serviceDialog} onClose={() => setServiceDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{serviceDialog?.service ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Mobile" value={serviceForm.mobile} onChange={(e) => setServiceForm({ ...serviceForm, mobile: e.target.value })} fullWidth />
          <TextField label="Email" value={serviceForm.email} onChange={(e) => setServiceForm({ ...serviceForm, email: e.target.value })} fullWidth />
          <TextField label="Country" value={serviceForm.country} onChange={(e) => setServiceForm({ ...serviceForm, country: e.target.value })} fullWidth />
          <TextField label="Address" value={serviceForm.address} onChange={(e) => setServiceForm({ ...serviceForm, address: e.target.value })} fullWidth />
          <TextField label="Timezone" value={serviceForm.timezone} onChange={(e) => setServiceForm({ ...serviceForm, timezone: e.target.value })} fullWidth />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setServiceDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveService}>{serviceDialog?.service ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Service Confirm */}
      <Dialog open={!!deleteServiceTarget} onClose={() => setDeleteServiceTarget(null)}>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent><Typography>Delete service {deleteServiceTarget?.id}?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteServiceTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteService}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
